import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Users, Clock, Calendar, UserPlus, DollarSign } from "lucide-react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const CARD_ACCENTS = ["#F59E0B", "#0A7AFF", "#FF3636", "#22C55E"];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0C0C10] border border-[#252530] px-3 py-2">
      <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-widest text-[#EAFF00] mb-1">{label}</p>
      <p className="font-['JetBrains_Mono'] text-sm text-[#EEEADF]">{payload[0].value}</p>
    </div>
  );
};

const leaveRequests = [
  { name: "Dr. Priya Nair", dept: "Computer Science", days: 2, from: "Mar 15", type: "Sick Leave", status: "pending" },
  { name: "Prof. Amit Desai", dept: "Electronics", days: 1, from: "Mar 18", type: "Personal", status: "pending" },
  { name: "Ms. Kavitha Rao", dept: "Mathematics", days: 3, from: "Mar 20", type: "Casual", status: "approved" },
];

const recentHires = [
  { name: "Suresh Iyer", role: "Asst. Professor", dept: "Civil Engg", joined: "Feb 1" },
  { name: "Meera Krishnan", role: "Lab Technician", dept: "Physics", joined: "Feb 10" },
  { name: "Rajiv Nair", role: "Administrator", dept: "Admin", joined: "Feb 15" },
];

export default function HRDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dashboard/stats`, { withCredentials: true })
      .then(r => setStats(r.data)).catch(() => {});
  }, []);

  const deptData = stats?.charts?.departments || [
    { dept: "Engg", count: 89 }, { dept: "Science", count: 64 },
    { dept: "Commerce", count: 52 }, { dept: "Arts", count: 47 }, { dept: "Admin", count: 37 }
  ];

  const statCards = [
    { label: "Total Employees", value: stats?.stats?.total_employees || 289, sub: "All departments" },
    { label: "On Leave Today", value: stats?.stats?.on_leave_today || 12, sub: "Out of office" },
    { label: "Pending Leaves", value: stats?.stats?.pending_leaves || 7, sub: "Needs approval" },
    { label: "Open Positions", value: stats?.stats?.open_positions || 5, sub: "Hiring active" },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} data-testid="hr-dashboard" className="space-y-6">
      <div className="border-b border-[#D8D4CB] pb-5">
        <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Human Resources</p>
        <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">HR Dashboard</h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <motion.div
            key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            data-testid={`hr-stat-${i}`}
            className="bg-white border border-[#E2DDD4] p-5 hover:border-[#0C0C10] transition-colors"
            style={{ borderLeft: `3px solid ${CARD_ACCENTS[i]}` }}
          >
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-3">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-[2.2rem] text-[#0C0C10] leading-none">{s.value}</p>
            <p className="font-['DM_Sans'] text-xs text-[#7A7468] mt-3">{s.sub}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E2DDD4] p-6">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-1">Staff by Department</p>
          <p className="font-['Syne'] font-bold text-sm text-[#0C0C10] mb-5">Headcount distribution</p>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={deptData}>
              <CartesianGrid strokeDasharray="2 4" stroke="#F0ECE4" />
              <XAxis dataKey="dept" tick={{ fontSize: 9, fill: "#BDBAA8", fontFamily: "Syne" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#BDBAA8", fontFamily: "Syne" }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" fill="#F59E0B" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-[#E2DDD4] p-6">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Today's Attendance</p>
          <div className="space-y-4">
            {[
              { label: "Present", value: 249, total: 289, color: "#22C55E" },
              { label: "On Leave", value: 12, total: 289, color: "#0A7AFF" },
              { label: "Absent", value: 28, total: 289, color: "#FF3636" },
            ].map((item, i) => (
              <div key={i}>
                <div className="flex justify-between mb-1.5">
                  <span className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{item.label}</span>
                  <span className="font-['JetBrains_Mono'] text-xs text-[#0C0C10] font-bold">{item.value}</span>
                </div>
                <div className="w-full h-1.5 bg-[#F0ECE4]">
                  <div className="h-1.5 transition-all duration-500" style={{ width: `${(item.value / item.total) * 100}%`, background: item.color }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 bg-[#F59E0B]/8 border border-[#F59E0B]/20 px-4 py-3 flex items-center gap-3">
            <DollarSign className="w-4 h-4 text-[#F59E0B] flex-shrink-0" strokeWidth={1.5} />
            <div>
              <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">Payroll Processed</p>
              <p className="font-['DM_Sans'] text-xs text-[#7A7468]">February — ₹68.4L disbursed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Leave requests */}
      <div className="bg-white border border-[#E2DDD4] p-6" data-testid="leave-requests">
        <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Leave Requests</p>
        <div className="space-y-2">
          {leaveRequests.map((l, i) => (
            <div key={i} className="flex items-center gap-4 p-3.5 bg-[#F5F2EA] border border-[#E2DDD4]">
              <div className="w-8 h-8 bg-[#F59E0B]/15 border border-[#F59E0B]/25 flex items-center justify-center flex-shrink-0">
                <span className="font-['Syne'] font-bold text-sm text-[#F59E0B]">{l.name.charAt(0)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{l.name}</p>
                <p className="font-['DM_Sans'] text-xs text-[#7A7468] truncate">{l.dept} — {l.type} — {l.days}d from {l.from}</p>
              </div>
              {l.status === "pending" ? (
                <div className="flex gap-2 flex-shrink-0">
                  <button data-testid={`approve-leave-${i}`} className="font-['Syne'] font-bold text-[9px] uppercase tracking-wider bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/25 px-2.5 py-1.5 hover:bg-[#22C55E]/20 transition-colors">Approve</button>
                  <button data-testid={`reject-leave-${i}`} className="font-['Syne'] font-bold text-[9px] uppercase tracking-wider bg-[#FF3636]/10 text-[#FF3636] border border-[#FF3636]/25 px-2.5 py-1.5 hover:bg-[#FF3636]/20 transition-colors">Reject</button>
                </div>
              ) : (
                <span className="font-['Syne'] font-bold text-[9px] uppercase tracking-wider text-[#22C55E] border border-[#22C55E]/25 px-2.5 py-1.5 flex-shrink-0">Approved</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Recent hires */}
      <div className="bg-white border border-[#E2DDD4] p-6" data-testid="recent-hires">
        <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Recent Hires</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {recentHires.map((h, i) => (
            <div key={i} className="flex items-center gap-3 p-3 border border-[#22C55E]/20 bg-[#22C55E]/5">
              <div className="w-8 h-8 bg-[#22C55E]/15 border border-[#22C55E]/25 flex items-center justify-center flex-shrink-0">
                <span className="font-['Syne'] font-bold text-sm text-[#22C55E]">{h.name.charAt(0)}</span>
              </div>
              <div className="min-w-0">
                <p className="font-['Syne'] font-bold text-xs text-[#0C0C10] truncate">{h.name}</p>
                <p className="font-['DM_Sans'] text-[10px] text-[#7A7468] truncate">{h.role}</p>
                <p className="font-['Syne'] font-bold text-[9px] text-[#22C55E] uppercase tracking-wider">Joined {h.joined}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
